/**
 * Minimal design tokens + light/dark palettes.
 *
 * A theme is applied by writing its variables onto :root as CSS custom
 * properties, so components style against `var(--rt-…)` and switch instantly.
 */

export type ThemeMode = 'light' | 'dark';

export interface ThemePalette {
  bg: string;
  surface: string;
  border: string;
  text: string;
  textMuted: string;
  primary: string;
  primaryText: string;
  danger: string;
  success: string;
}

export const lightPalette: ThemePalette = {
  bg: '#f7f8fa',
  surface: '#ffffff',
  border: '#e2e5ea',
  text: '#1a1d23',
  textMuted: '#6b7280',
  primary: '#2563eb',
  primaryText: '#ffffff',
  danger: '#dc2626',
  success: '#16a34a',
};

export const darkPalette: ThemePalette = {
  bg: '#0d1117',
  surface: '#161b22',
  border: '#30363d',
  text: '#e6edf3',
  textMuted: '#8b949e',
  primary: '#3b82f6',
  primaryText: '#ffffff',
  danger: '#f87171',
  success: '#4ade80',
};

export const palettes: Record<ThemeMode, ThemePalette> = {
  light: lightPalette,
  dark: darkPalette,
};

/** Write a palette to :root as `--rt-<token>` CSS variables. */
export function applyTheme(mode: ThemeMode): void {
  const palette = palettes[mode];
  const root = document.documentElement;
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
  (Object.entries(palette) as [keyof ThemePalette, string][]).forEach(
    ([key, value]) => {
      root.style.setProperty(`--rt-${kebab(key)}`, value);
    },
  );
}

/** Detect the user's preferred mode from the OS. */
export function preferredMode(): ThemeMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}
